import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Image, InfiniteScroll, NavBar, Popup, PullToRefresh, SearchBar, Selector, Toast } from 'antd-mobile';
import { AddOutline, CloseOutline, DeleteOutline } from 'antd-mobile-icons';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import API_ROUTES from '../api';
import Icon from '../icon';
import './index.css';

const typeMeta = {
    marriage: { label: '结婚', icon: '💍' },
    baby: { label: '生子', icon: '🍼' },
    paper: { label: '大文章', icon: '📄' },
    project: { label: '大课题', icon: '🧪' },
    transfer: { label: '转化', icon: '🚀' },
    award: { label: '表彰', icon: '🏅' },
    other: { label: '其他', icon: '✨' },
};

// 与球队相册使用相同的队员身份和排序规则，保证两个筛选弹窗的内容顺序一致。
const getMemberTypeMeta = (tempMember) => {
    if (tempMember === null || tempMember === undefined || tempMember === '') return { label: '常驻', order: 0 };
    if (Number(tempMember) === 3) return { label: '非正式', order: 1 };
    if (Number(tempMember) === 4) return { label: '离队', order: 2 };
    if (Number(tempMember) === 1) return { label: '临时', order: 3 };
    return { label: '其他', order: 4 };
};

const normalizeMemberRecords = (records) => {
    return (Array.isArray(records) ? records : [])
        // temp_member = 2 表示外战队伍，不属于人员姓名筛选范围。
        .filter(item => item?.id && Number(item.tempMember ?? item.temp_member) !== 2)
        .map(item => {
            const name = item.name || item.memberName || item.userName || '队友';
            const typeMetaInfo = getMemberTypeMeta(item.tempMember ?? item.temp_member);
            return {
                label: name,
                value: item.id,
                searchText: `${name} ${typeMetaInfo.label}`,
                typeLabel: typeMetaInfo.label,
                typeOrder: typeMetaInfo.order
            };
        })
        .sort((a, b) => a.typeOrder - b.typeOrder || String(a.label).localeCompare(String(b.label), 'zh-Hans-CN'));
};

export default function HappyEvent() {
    const navigate = useNavigate();
    const location = useLocation();
    const memberInfo = useMemo(() => JSON.parse(localStorage.getItem('memberInfo') || '{}'), []);
    const isAdmin = Number(memberInfo?.admin) === 1;
    const initialMemberId = location.state?.memberId || 'all';
    const initialMemberName = location.state?.memberName || '';
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageCurrent, setPageCurrent] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [typeFilter, setTypeFilter] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [titleKeyword, setTitleKeyword] = useState('');
    const [tagOptions, setTagOptions] = useState([]);
    const [detail, setDetail] = useState(null);
    const [memberOptions, setMemberOptions] = useState([{ label: '全部队员', value: 'all', searchText: '全部队员' }]);
    const [selectedMember, setSelectedMember] = useState(initialMemberId);
    const [memberPickerVisible, setMemberPickerVisible] = useState(false);
    const [memberPickerKeyword, setMemberPickerKeyword] = useState('');
    const [memberPickerDraft, setMemberPickerDraft] = useState(initialMemberId);

    const getMediaUrl = (item) => item?.mediaUrl || item?.media_url || item?.url || '';
    const getMediaType = (item) => Number(item?.mediaType ?? item?.media_type);

    const buildThumbUrl = (item) => {
        const url = getMediaUrl(item);
        if (!url) return '';
        if (getMediaType(item) === 2) {
            return `${url}${url.includes('?') ? '&' : '?'}x-oss-process=video/snapshot,t_10000,m_fast`;
        }
        return url + Icon.ImageFooter;
    };

    const getTypeMeta = (type) => typeMeta[type] || typeMeta.other;

    const typeOptions = useMemo(() => {
        return [{ label: '全部', value: 'all' }, ...tagOptions.map(tag => ({ label: tag.tagName, value: tag.id }))];
    }, [tagOptions]);

    const getHappyTypeMeta = (type) => {
        const backendTag = tagOptions.find(tag => String(tag.id) === String(type));
        if (backendTag) {
            return { label: backendTag.tagName, icon: typeMeta[type]?.icon || '✨' };
        }
        return getTypeMeta(type);
    };

    const pageSize = 10;

    const selectedMemberLabel = useMemo(() => {
        if (selectedMember === initialMemberId && initialMemberName) return initialMemberName;
        return memberOptions.find(item => String(item.value) === String(selectedMember))?.label || '全部队员';
    }, [initialMemberId, initialMemberName, memberOptions, selectedMember]);

    const filteredMemberOptions = useMemo(() => {
        const keyword = memberPickerKeyword.trim().toLowerCase();
        if (!keyword) return memberOptions;
        return memberOptions.filter(item => String(item.searchText || item.label || '').toLowerCase().includes(keyword));
    }, [memberOptions, memberPickerKeyword]);

    const getHappyList = useCallback((current = 1, reset = true) => {
        if (reset) {
            setLoading(true);
        }
        return axios.get(API_ROUTES.happy_list, {
            params: {
                current,
                size: pageSize,
                eventType: typeFilter === 'all' ? undefined : typeFilter,
                title: titleKeyword.trim() || undefined,
                // 从个人主页进入时始终限定为该球员关联的喜事。
                memberId: selectedMember === 'all' ? undefined : selectedMember,
                status: 0
            }
        }).then((response) => {
            const pageData = response.data?.data || {};
            const records = response.data?.data?.records || [];
            const nextRecords = Array.isArray(records) ? records : [];
            setList(prev => reset ? nextRecords : [...prev, ...nextRecords]);
            setPageCurrent(current);
            setTotalCount(Number(pageData.total || 0));
            setHasMore(nextRecords.length >= pageSize && current * pageSize < Number(pageData.total || 0));
        }).catch(() => {
            Toast.show({ icon: 'fail', content: '喜事列表加载失败' });
        }).finally(() => {
            if (reset) setLoading(false);
        });
    }, [selectedMember, titleKeyword, typeFilter]);

    const getMemberOptions = useCallback(() => {
        const yearNum = new Date().getFullYear();
        return axios.get(API_ROUTES.soccer_list, {
            params: { year: yearNum, type: 1 }
        }).then(response => {
            const data = response.data?.data || [];
            // 成员接口可能按身份分组返回，先拍平成相册同款的选择项。
            const records = Array.isArray(data) && data[0]?.items
                ? data.flatMap(group => group.items || [])
                : data;
            setMemberOptions([
                { label: '全部队员', value: 'all', searchText: '全部队员' },
                ...normalizeMemberRecords(records)
            ]);
        }).catch(() => {
            setMemberOptions([{ label: '全部队员', value: 'all', searchText: '全部队员' }]);
        });
    }, []);

    const loadHappyTags = () => {
        return axios.get(API_ROUTES.happy_tag_list).then((response) => {
            const records = response.data?.data || [];
            const nextTags = Array.isArray(records) ? records : [];
            setTagOptions(nextTags);
        }).catch(() => {
            Toast.show({ icon: 'fail', content: '喜事标签加载失败' });
        });
    };

    useEffect(() => {
        // 筛选或搜索变化时只重新拉取第一页，后续由 InfiniteScroll 继续加载。
        getHappyList(1, true);
    }, [getHappyList]);

    useEffect(() => {
        // 标题搜索做轻量防抖，减少快速输入时的重复请求。
        const timer = setTimeout(() => setTitleKeyword(searchText), 350);
        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        loadHappyTags();
        getMemberOptions();
    }, [getMemberOptions]);

    useEffect(() => {
        const happyId = location.state?.happyId;
        if (!happyId) return;
        // 从独立聚餐页跳转过来时，直接打开对应喜事详情弹窗。
        axios.get(API_ROUTES.happy_detail, { params: { id: happyId } }).then((response) => {
            if (response.data?.code === 200 && response.data?.data) {
                setDetail(response.data.data);
            }
        });
    }, [location.state?.happyId]);

    const openCreate = () => {
        navigate('/happyEventCreate');
    };

    const openMemberPicker = () => {
        setMemberPickerDraft(selectedMember);
        setMemberPickerKeyword('');
        setMemberPickerVisible(true);
    };

    const confirmMemberPicker = () => {
        setSelectedMember(memberPickerDraft || 'all');
        setMemberPickerVisible(false);
    };

    const deleteHappy = (item) => {
        axios.post(API_ROUTES.happy_delete, null, { params: { id: item.id } }).then((response) => {
            if (response.data?.code === 200) {
                Toast.show({ icon: 'success', content: '已删除' });
                setDetail(null);
                getHappyList();
            } else {
                Toast.show({ icon: 'fail', content: response.data?.msg || '删除失败' });
            }
        });
    };

    return (
        <div className="happy-page has-unified-nav">
            <NavBar className="unified-navbar fixed" onBack={() => initialMemberId !== 'all' ? navigate(-1) : navigate('/home')}>
                {selectedMember !== 'all' ? `${selectedMemberLabel}的科朋喜事` : '科朋喜事'}
            </NavBar>
            <PullToRefresh onRefresh={getHappyList}>
                <section className="happy-hero">
                    <div>
                        <span className="happy-kicker">HUAXI SOCCER MOMENTS</span>
                        <h1>记录值得举杯的时刻</h1>
                        <p>结婚、生子、文章、课题、转化和表彰，都可以和队友、比赛、聚餐一起归档。</p>
                    </div>
                    <div className="happy-hero-icon">🎉</div>
                </section>

                <section className="happy-filter">
                    <SearchBar
                        className="happy-search"
                        placeholder="输入喜事标题搜索，例如：结婚、文章"
                        value={searchText}
                        onChange={setSearchText}
                    />
                    <button className="happy-member-filter" type="button" onClick={openMemberPicker}>
                        <span>{selectedMemberLabel}</span>
                        <i>筛选队员</i>
                    </button>
                    <Selector
                        columns={4}
                        value={[typeFilter]}
                        options={typeOptions}
                        onChange={(value) => setTypeFilter(value[0] || 'all')}
                    />
                </section>

                <div className="happy-section-head">
                    <div>
                        <strong>{selectedMember !== 'all' ? `与${selectedMemberLabel}相关` : '喜事列表'}</strong>
                        <span>{loading ? '加载中...' : `已展示 ${list.length}${totalCount ? ` / ${totalCount}` : ''} 条`}</span>
                    </div>
                    {isAdmin && (
                        <Button size="small" className="happy-create-entry" onClick={openCreate}>
                            <AddOutline /> 记录喜事
                        </Button>
                    )}
                </div>

                {list.length ? (
                    <div className="happy-list">
                        {list.map(item => {
                            const meta = getHappyTypeMeta(item.eventType);
                            const cover = item.coverMedia || item.mediaList?.[0];
                            return (
                                <article key={item.id} className="happy-card" onClick={() => setDetail(item)}>
                                    <div className="happy-cover">
                                        {cover ? (
                                            <img src={buildThumbUrl(cover)} alt={item.title} />
                                        ) : (
                                            <div className="happy-cover-empty">{meta.icon}</div>
                                        )}
                                        <span>{meta.icon} {item.eventTypeName || meta.label}</span>
                                    </div>
                                    <div className="happy-card-body">
                                        {item.dinnerId && (
                                            <button
                                                type="button"
                                                className="happy-dinner-badge"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    // 有绑定比赛时跳到比赛详情里的聚餐信息；无比赛时进入聚餐列表查看。
                                                    if (item.gameId) {
                                                        navigate(`/games/${item.gameId}/history`, { state: { activeTab: 'dinner' } });
                                                    } else {
                                                        navigate(`/dinners/${item.dinnerId}`);
                                                    }
                                                }}
                                            >
                                                🍽️ 已安排聚餐 <i>查看</i>
                                            </button>
                                        )}
                                        <h3>{item.title}</h3>
                                        <p>{item.content || '暂无描述'}</p>
                                        <div className="happy-members">{item.memberNames?.join('、') || '未关联队员'}</div>
                                        <div className="happy-meta-row">
                                            {item.eventDate && <em>{item.eventDate}</em>}
                                            {item.dinnerId && <em className="dinner">聚餐</em>}
                                            {item.gameName && <em>{item.gameName}</em>}
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                        <InfiniteScroll
                            loadMore={() => getHappyList(pageCurrent + 1, false)}
                            hasMore={hasMore}
                        >
                            {hasMore ? '继续加载喜事...' : '已经到底部了'}
                        </InfiniteScroll>
                    </div>
                ) : (
                    <Empty description={loading ? '正在加载' : '暂无喜事记录'} imageStyle={{ width: 90 }} />
                )}
            </PullToRefresh>

            <Popup visible={!!detail} onMaskClick={() => setDetail(null)} bodyStyle={{ borderTopLeftRadius: 22, borderTopRightRadius: 22, maxHeight: '88vh', overflowY: 'auto' }}>
                {detail && (
                    <div className="happy-detail">
                        <div className="happy-detail-head">
                            <div>
                                <span>{getHappyTypeMeta(detail.eventType).icon} {detail.eventTypeName || getHappyTypeMeta(detail.eventType).label}</span>
                                <h2>{detail.title}</h2>
                            </div>
                            <button type="button" onClick={() => setDetail(null)}><CloseOutline /></button>
                        </div>
                        <p className="happy-detail-content">{detail.content || '暂无描述'}</p>
                        <div className="happy-detail-info">
                            <div><span>关联队员</span><strong>{detail.memberNames?.join('、') || '未关联'}</strong></div>
                            <div><span>发生日期</span><strong>{detail.eventDate || '未填写'}</strong></div>
                            <div><span>关联比赛</span><strong>{detail.gameName || '未关联'}</strong></div>
                            <div className={detail.dinnerId ? 'happy-detail-dinner-row' : ''}>
                                <span>关联聚餐</span>
                                <strong>
                                    {detail.dinnerInfo?.remark || (detail.dinnerId ? '已关联聚餐' : '未关联')}
                                    {detail.dinnerId && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (detail.gameId) {
                                                    navigate(`/games/${detail.gameId}/history`, { state: { activeTab: 'dinner' } });
                                                } else {
                                                    navigate(`/dinners/${detail.dinnerId}`);
                                                }
                                            }}
                                        >
                                            去查看
                                        </button>
                                    )}
                                </strong>
                            </div>
                            {detail.dinnerInfo?.position && <div><span>聚餐地点</span><strong>{detail.dinnerInfo.position}</strong></div>}
                        </div>
                        {detail.mediaList?.length > 0 && (
                            <div className="happy-media-grid">
                                {detail.mediaList.map(media => getMediaType(media) === 2 ? (
                                    <video key={media.id} src={getMediaUrl(media)} poster={buildThumbUrl(media)} controls />
                                ) : (
                                    <Image key={media.id} src={getMediaUrl(media)} fit="cover" />
                                ))}
                            </div>
                        )}
                        {isAdmin && (
                            <div className="happy-detail-actions">
                                <Button
                                    block
                                    color="primary"
                                    onClick={() => navigate('/happyEventCreate', { state: { happyId: detail.id } })}
                                >
                                    修改内容
                                </Button>
                                <Button block color="danger" fill="outline" onClick={() => deleteHappy(detail)}>
                                    <DeleteOutline /> 删除这条喜事
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </Popup>

            <Popup
                visible={memberPickerVisible}
                onMaskClick={() => setMemberPickerVisible(false)}
                bodyStyle={{ borderTopLeftRadius: 18, borderTopRightRadius: 18, overflow: 'hidden' }}
            >
                <div className="happy-member-picker-popup">
                    <div className="happy-member-picker-head">
                        <button type="button" onClick={() => setMemberPickerVisible(false)}>取消</button>
                        <strong>筛选关联队员</strong>
                        <button type="button" onClick={confirmMemberPicker}>确认</button>
                    </div>
                    <SearchBar
                        className="happy-member-picker-search"
                        placeholder="搜索队员姓名"
                        value={memberPickerKeyword}
                        onChange={setMemberPickerKeyword}
                    />
                    <div className="happy-member-picker-grid">
                        {filteredMemberOptions.length > 0 ? filteredMemberOptions.map(item => (
                            <button
                                key={item.value}
                                type="button"
                                className={`happy-member-picker-option ${String(memberPickerDraft) === String(item.value) ? 'active' : ''}`}
                                onClick={() => setMemberPickerDraft(item.value)}
                            >
                                <strong>{item.label}</strong>
                                {item.typeLabel && <span className={`type-${item.typeOrder}`}>{item.typeLabel}</span>}
                                {String(memberPickerDraft) === String(item.value) && <i>已选</i>}
                            </button>
                        )) : (
                            <Empty description="没有找到队员" imageStyle={{ width: 72 }} />
                        )}
                    </div>
                </div>
            </Popup>

        </div>
    );
}
